/**
 * AppComponent - Application Shell
 * 
 * PRIMARY RESPONSIBILITIES:
 * 1. Provide the application shell (router-outlet, side menu structure)
 * 2. Manage side menu state (open/close, active item highlighting)
 * 3. Build and update menu based on user role and account status
 * 4. Handle menu item clicks and delegate navigation to router
 * 5. Subscribe to global app events (login, page loaded, connectivity)
 * 6. Manage global UI state (menu visibility, active page title)
 * 7. Handle logout confirmation and execution
 * 
 * DOES NOT:
 * - Perform authentication (delegated to AuthService)
 * - Decide navigation routes after login (delegated to WelcomePage)
 * - Load business data (delegated to individual pages)
 * - Handle route protection (delegated to AuthGuard)
 * 
 * LIFECYCLE:
 * - Bootstrapped by Angular in main.ts as the root component
 * - Lives for the entire application lifecycle
 * - Destroyed only when app closes
 * 
 * EVENT FLOW:
 * 1. User logs in via WelcomePage
 * 2. WelcomePage publishes EVENT_LOGIN
 * 3. AppComponent receives event and updates menu structure
 * 4. WelcomePage handles navigation to appropriate page
 */
/* eslint-disable no-console */

import { Component, Injector, HostListener, ChangeDetectorRef } from '@angular/core';
import type { OnInit, OnDestroy } from '@angular/core';
import { Platform, NavController, MenuController, IonicModule } from '@ionic/angular';
import { Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { filter } from 'rxjs/operators';

import { AuthService, CapacitorService, LocalStorageService, MessageCenterService, MenuService } from '@shared/services';
import { ViewBasePage } from '@shared/pages';
import { Constants } from '@shared/models';
import type { TMessage } from '@shared/models';
import type { Subscription } from 'rxjs';

type TMenuPage = {
    title: string;
    url: string;
    icon: string;
    testid: string;
}

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: true,
    imports: [ IonicModule, CommonModule, FormsModule ]
})
export class AppComponent extends ViewBasePage implements OnInit, OnDestroy {
    public vm = {
        version: Constants.APPLICATION_VERSION,
        dealershipName: '',
        user: {
            firstName: '',
            role: '',
            uid: ''
        },
        unreadMessagesCount: 0
    };
    
    public activePageTitle = 'Dashboard';
    public appPages: TMenuPage[] = [];
    
    private menuController: MenuController;
    private previousMenuitemTitle = '';
    private messagesSubscription: Subscription | null = null;

    constructor(
        private platform: Platform,
        private navController: NavController,
        // 
        public authService: AuthService,
        private capacitorService: CapacitorService,
        menuController: MenuController,
        private menuService: MenuService,
        private localStorageService: LocalStorageService,
        private messageCenterService: MessageCenterService,
        private cdr: ChangeDetectorRef,
        injector: Injector
    ) {
        super(injector);
        this.menuController = menuController;
    }

    public override async ngOnInit(): Promise<void> {
        super.ngOnInit();

        // Initialize local storage for persisting user preferences
        await this.localStorageService.initialize();
        
        // Remove focus on navigation to fix ARIA accessibility warnings
        this.removeFocusOnHiddenPages();
        
        // Subscribe to global application events
        this.subscribeToAppEvents();
    }

    public override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.unsubscribeAll();
        this.menuService.cleanup();
    }

    /**
     * Subscribe to global application events
     * - LOGIN: Update menu structure when user logs in
     * - PAGE_LOADED: Track active page and save to local storage
     * - INTERNET_CONNECTION_CHANGE: Show connectivity status
     * - BACK_TO_PREVIOUS_PAGE: Handle navigation to previous page
     */
    private subscribeToAppEvents(): void {
        this.eventsService.subscribe(Constants.EVENT_LOGIN, () => {
            this.onLoginEvent();
        });
        
        this.eventsService.subscribe(Constants.EVENT_PAGE_LOADED, (viewName: string) => {
            this.onPageLoadedEvent(viewName);
        });
        
        this.eventsService.subscribe(Constants.EVENT_INTERNET_CONNECTION_CHANGE, async (connected: any) => {
            this.onInternetConnectionChange(connected);
        });
        
        this.eventsService.subscribe(Constants.EVENT_BACK_TO_PREVIOUS_PAGE, () => {
            this.onBackToPreviousPageEvent();
        });
    }

    /**
     * Remove focus from elements during navigation
     * Fixes ARIA accessibility warnings when Ionic adds aria-hidden to pages
     */
    private removeFocusOnHiddenPages(): void {
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe(() => {
            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }
        });
    }

    /**
     * Handle LOGIN event
     * Updates menu structure based on user role and account status
     * DOES NOT handle navigation - that's done by WelcomePage
     */
    private async onLoginEvent(): Promise<void> {
        try {
            this.menuService.initialize();

            const dealership = this.sessionService.getDealership();
            const user = this.sessionService.getCurrentUser();
            const status = this.sessionService.getAccountStatus();
            
            // Update view model with user info
            this.vm.dealershipName = dealership?.dealershipName || '';
            this.vm.user = user;

            // Build menu structure based on account status and user role
            if (this.isAccountExpired(status)) {
                this.appPages = this.getExpiredPages();
            } else {
                this.appPages = this.getPagesByRole(user.role);
            }

            // Subscribe to messages for unread count badge
            this.subscribeToMessages();

            // Force change detection to update menu UI
            this.cdr.detectChanges();
            
            console.log('✅ Menu updated with', this.appPages.length, 'items for role:', user.role);

            // ✅ Wait for menu to actually render in DOM
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // ✅ Ensure menu is closed
            await this.menuController.close();
            
            // ✅ Check if router outlet has aria-hidden
            const routerOutlet = document.querySelector('ion-router-outlet');
            if (routerOutlet?.getAttribute('aria-hidden') === 'true') {
                console.log('🔧 Removing aria-hidden from router outlet');
                routerOutlet.removeAttribute('aria-hidden');
            }            
        } catch (err) {
            console.error('❌ Error in onLoginEvent:', err);
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Error', errorMessage);
        }
    }

    /**
     * Subscribe to messages to show unread count badge on Help menu item.
     * Uses filtered observable to only count messages targeted to current user.
     */
    private subscribeToMessages(): void {
        // Unsubscribe from previous subscription if exists
        if (this.messagesSubscription) {
            this.messagesSubscription.unsubscribe();
        }

        this.messagesSubscription = this.messageCenterService.getFilteredObservable()
            .subscribe((messages: TMessage[]) => {
                // Count unread, non-dismissed messages
                this.vm.unreadMessagesCount = messages.filter(
                    // m => m.status === 'unread' && m.status !== 'dismissed'
                    m => m.status === 'unread'
                ).length;
                this.cdr.detectChanges();
            });
    }

    /**
     * Check if account is expired or cancelled
     */
    private isAccountExpired(status: string): boolean {
        const expiredStatuses = [
            'Cancelled By Customer',
            'Cancelled By Support',
            'Trial Period Expired',
            'Package Expired'
        ];
        return expiredStatuses.includes(status);
    }

    /**
     * Get menu pages based on user role
     */
    private getPagesByRole(role: string): TMenuPage[] {
        switch (role) {
            case 'marketer':
                return this.getMarketerPages();
            case 'serviceAdvisor':
                return this.getServiceAdvisorPages();
            case 'driver':
                return this.getDriverPages();
            case 'manager':
                return this.getManagerPages();
            default:
                console.error('Invalid user role:', role);
                return this.getExpiredPages();
        }
    }

    /**
     * Menu pages for expired/cancelled accounts
     */
    private getExpiredPages(): TMenuPage[] {
        return [
            { title: 'Account', url: 'account', icon: 'card', testid: 'account' },
            { title: 'About', url: 'about', icon: 'information-circle-sharp', testid: 'about' },
            { title: 'Logout', url: '', icon: 'close-sharp', testid: 'logout' }
        ];
    }

    /**
     * Menu pages for Service Advisor role
     */
    private getServiceAdvisorPages(): TMenuPage[] {
        return [
            { title: 'Dashboard', url: 'dashboard', icon: 'stats-chart-sharp', testid: 'dashboard' },
            { title: 'Tickets', url: 'tickets', icon: 'pricetags-sharp', testid: 'tickets' },
            { title: 'Tracker', url: 'tracker', icon: 'locate-sharp', testid: 'tracker' },
            { title: 'Settings', url: 'userSettings', icon: 'construct-sharp', testid: 'userSettings' },
            { title: 'Account', url: 'account', icon: 'card', testid: 'account' },
            { title: 'About', url: 'about', icon: 'information-sharp', testid: 'about' },
            { title: 'Help', url: 'help', icon: 'help-sharp', testid: 'help' },
            { title: 'Logout', url: '', icon: 'close-sharp', testid: 'logout' },
            { title: 'Website', url: 'www.shuttlenomix.com', icon: 'globe-outline', testid: 'website' }
        ];
    }

    /**
     * Menu pages for Driver role
     */
    private getDriverPages(): TMenuPage[] {
        return [
            { title: 'Dashboard', url: 'dashboard', icon: 'stats-chart-sharp', testid: 'dashboard' },
            { title: 'Routes', url: 'routes', icon: 'color-wand-sharp', testid: 'routes' },
            { title: 'Navigation', url: 'navigation', icon: 'map-sharp', testid: 'navigation' },
            { title: 'Settings', url: 'userSettings', icon: 'construct-sharp', testid: 'userSettings' },
            { title: 'Account', url: 'account', icon: 'card', testid: 'account' },
            { title: 'About', url: 'about', icon: 'information-sharp', testid: 'about' },
            { title: 'Help', url: 'help', icon: 'help-sharp', testid: 'help' },
            { title: 'Logout', url: '', icon: 'close-sharp', testid: 'logout' },
            { title: 'Website', url: 'www.shuttlenomix.com', icon: 'globe-outline', testid: 'website' }
        ];
    }

    /**
     * Menu pages for Manager role
     */
    private getManagerPages(): TMenuPage[] {
        return [
            { title: 'Dashboard', url: 'dashboard', icon: 'stats-chart-sharp', testid: 'dashboard' },
            { title: 'Tickets', url: 'tickets', icon: 'pricetags-sharp', testid: 'tickets' },
            { title: 'Routes', url: 'routes', icon: 'color-wand-sharp', testid: 'routes' },
            { title: 'Navigation', url: 'navigation', icon: 'map-sharp', testid: 'navigation' },
            { title: 'Tracker', url: 'tracker', icon: 'locate-sharp', testid: 'tracker' },
            { title: 'Marketing', url: 'marketing', icon: 'cash-sharp', testid: 'marketing' },
            { title: 'Reports', url: 'reports/report1Type', icon: 'documents-sharp', testid: 'reports' },
            { title: 'Settings', url: 'managerSettings', icon: 'construct-sharp', testid: 'managerSettings' },
            { title: 'Account', url: 'account', icon: 'card', testid: 'account' },
            { title: 'About', url: 'about', icon: 'information-sharp', testid: 'about' },
            { title: 'Help', url: 'help', icon: 'help-sharp', testid: 'help' },
            { title: 'Logout', url: '', icon: 'close-sharp', testid: 'logout' },
            { title: 'Website', url: 'www.shuttlenomix.com', icon: 'globe-outline', testid: 'website' }
        ];
    }

    /**
     * Menu pages for Marketer role
     */
    private getMarketerPages(): TMenuPage[] {
        return [
            { title: 'Dashboard', url: 'dashboard', icon: 'stats-chart-sharp', testid: 'dashboard' },
            { title: 'Marketing', url: 'marketing', icon: 'cash-sharp', testid: 'marketing' },
            { title: 'Account', url: 'account', icon: 'card', testid: 'account' },
            { title: 'About', url: 'about', icon: 'information-sharp', testid: 'about' },
            { title: 'Help', url: 'help', icon: 'help-sharp', testid: 'help' },
            { title: 'Logout', url: '', icon: 'close-sharp', testid: 'logout' },
            { title: 'Website', url: 'www.shuttlenomix.com', icon: 'globe-outline', testid: 'website' }
        ];
    }

    /**
     * Handle menu item clicks
     * Delegates navigation to Angular Router
     */
    public async onMenuItemClick(menuitemTitle: string): Promise<void> {
        await this.closeSidemenu();
        this.previousMenuitemTitle = menuitemTitle;

        try {
            const navigateToPage = this.appPages.find((page) => page.title === menuitemTitle);
            if (navigateToPage) {
                switch (navigateToPage.title) {
                    case 'Website':
                        await this.capacitorService.openWebsite();
                        break;

                    case 'Logout':
                        await this.confirmLogout();
                        break;

                    default:
                        await this.uxHelperService.showSpinner();
                        this.activePageTitle = navigateToPage.title;
                        await this.router.navigateByUrl(navigateToPage.url);
                        await this.uxHelperService.hideSpinner();
                        break;
                }
            } 
            // await this.closeSidemenu();
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Error', errorMessage);
        }
    }

    // If the dealership doesn't exist or if either the users or shuttles or service areas haven't been defined yet,
    // then disable most of the menuitems.  In other words, do not allow access to tickets, routes, navigation, tracker or
    // reports until the minimal settings have been defined.
    public isDisabled(): boolean {
        if (this.vb.disabled) {
            return true;
        }
        const exists = !this.menuService.getHasAllSettingsData();
        return exists;
    }

    /**
     * Close the side menu
     * Prevents issues with Cypress testing when menu stays open
     */
    private async closeSidemenu(): Promise<void> {
        console.log('🔴 Attempting to close sidemenu...');
        
        // Check if menu is open according to MenuController
        const isOpen = await this.menuController.isOpen('mainMenu');
        console.log('📊 MenuController thinks menu isOpen:', isOpen);
        
        if (isOpen) {
            // Normal path: MenuController knows menu is open
            const closed = await this.menuController.close('mainMenu');
            console.log('✅ Closed via MenuController:', closed);
            if (closed) {
                await new Promise(resolve => setTimeout(resolve, 350));
            }
        } else {
            // MenuController thinks it's closed but it might not be (state desync)
            console.warn('⚠️ MenuController thinks menu is closed, forcing close at DOM level');
            
            // Get the menu element directly and call its close method
            const menuElement = document.querySelector('ion-menu') as any;
            if (menuElement && typeof menuElement.close === 'function') {
                console.log('🔧 Calling ion-menu.close() directly');
                await menuElement.close();
                await new Promise(resolve => setTimeout(resolve, 350));
                console.log('✅ Forced menu close complete');
            } else {
                console.error('❌ Could not find ion-menu element');
            }
        }
    }

    /**
     * Handle PAGE_LOADED event
     * Updates active page title and saves to local storage
     */
    private async onPageLoadedEvent(viewName: string): Promise<void> {
        // Find the page in all possible menu structures
        const allPages = [
            ...this.getManagerPages(),
            ...this.getServiceAdvisorPages(),
            ...this.getDriverPages()
        ];

        const item = allPages.find((page) => 
            page.title.toLowerCase() === viewName.toLowerCase()
        );

        if (item) {
            this.activePageTitle = item.title;
            await this.localStorageService.set('lastActivePage', item.url);
        }
    }

    /**
     * Handle internet connectivity changes
     * Shows toast notification to user
     */
    private onInternetConnectionChange(connected: boolean): void {
        const msg = connected 
            ? 'Internet Connection Available.' 
            : 'Internet Connection Not Available.';
        this.uxHelperService.showToast(msg);
    }

    /**
     * Handle BACK_TO_PREVIOUS_PAGE event
     * Navigates to the previously visited page
     */
    public onBackToPreviousPageEvent(): void {
        if (this.previousMenuitemTitle) {
            this.onMenuItemClick(this.previousMenuitemTitle).catch((err) => {
                console.error('Navigation error:', err);
            });
        }
    }

    /**
     * Confirm and execute logout
     * Reloads page to clear all state and avoid Firebase permission errors
     * See: https://medium.com/@dalenguyen/handle-missing-or-insufficient-permissions-firestore-error-on-angular-or-ionic-bf4edb7a7c68
     */
    public async confirmLogout(): Promise<void> {
        const title = 'Confirm Logout';
        const message = 'Are you sure you want to log out?';
        
        const response = await this.uxHelperService.showAlert(title, message);
        if (response.okay) {
            await this.authService.logout();
            this.activePageTitle = '';
            await this.navController.navigateRoot('/welcome');
            window.location.reload();
        }
    }

    /**
     * Check if a menu item should be highlighted as active
     */
    public checkActivePage(menuitemTitle: string): boolean {
        return this.activePageTitle === menuitemTitle;
    }

    /**
     * Check if a menu item should be hidden
     * Used for conditional rendering in the template
     */
    public hideMenuitem(menuitemTitle: string): boolean {
        if (!menuitemTitle) {
            return false;
        }
        const menuitem = this.appPages.find((page: TMenuPage) => page.title === menuitemTitle);
        return !menuitem;
    }

    /**
     * Keyboard shortcuts for testing/debugging
     * Only active when debug mode is enabled
     */
    @HostListener('window:keydown', ['$event'])
    public handleKeyboardEventkeydown(event: KeyboardEvent): void {
        if (this.sessionService?.isDebugMode()) {
            const { altKey, ctrlKey, shiftKey, code } = event;

            // Ctrl+Shift+Alt+\ : Navigate to dashboard
            if (shiftKey && ctrlKey && altKey && code === 'Backslash') {
                this.router.navigateByUrl('/dashboard');
            }

            // Ctrl+Shift+Alt+F4 : Close side menu
            if (shiftKey && ctrlKey && altKey && code === 'F4') {
                this.closeSidemenu();
            }
        }
    }

    /**
     * Clean up event subscriptions
     */
    private unsubscribeAll(): void {
        this.eventsService.unsubscribe(Constants.EVENT_LOGIN);
        this.eventsService.unsubscribe(Constants.EVENT_LOGOUT);
        this.eventsService.unsubscribe(Constants.EVENT_PAGE_LOADED);
        this.eventsService.unsubscribe(Constants.EVENT_INTERNET_CONNECTION_CHANGE);
        this.eventsService.unsubscribe(Constants.EVENT_BACK_TO_PREVIOUS_PAGE);
        
        // Unsubscribe from messages subscription
        if (this.messagesSubscription) {
            this.messagesSubscription.unsubscribe();
            this.messagesSubscription = null;
        }
    }
}